"use client";

import {
  FC,
  memo,
  useEffect,
  useState,
  Suspense,
} from "react";
import { usePathname } from "next/navigation";
import { useTranslations } from "next-intl";

import DefaultHead from "apps/website/components/page/DefaultHead/DefaultHead";
import { makePageTags } from "apps/website/utils/website/head";
import Header from "apps/website/components/page/Header/Header";
import {
  HEADER_LINKS_NONE,
} from "apps/website/components/page/Header/Header.map";
import {
  SIGNUP_FLOW_PAGES,
  SignupFlowSlug,
} from "libs/state/src/lib/stores/signupFlow/constants";
import {
  useSignupFlowStore,
} from "libs/state/src/lib/stores/signupFlow/hooks/useSignUpFlowStore";
import { useFlowNavigation } from "@./state";
import { getInterpolationVariables } from "libs/utils/sign-up/fresh";

import Main from "../components/layout/Main/Main";
import {
  SimpleProgressSteps,
} from "../components/feature/Navigation/SimpleProgressSteps/SimpleProgressSteps";
import {
  signupSteps,
} from "../components/feature/Navigation/SimpleProgressSteps/signupSteps";
import Footer from "../components/page/Footer/Footer";
import Spacer from "../components/layout/Spacer/Spacer";
import TextTitle from "../components/base/Text/TextTitle/TextTitle";
import { themeRootClassMap } from "../maps/Theme.map";
import cx from "../utils/misc/cx";
import {
  QueryParamHandler,
} from "../components/sign-up/fresh/common/QueryParamHandler/QueryParamHandler";
import {
  LoadingSpinner,
} from "../components/form/LoadingSpinner/LoadingSpinner";
import TextSubtitle from "../components/base/Text/TextSubtitle/TextSubtitle";

import { ILayout } from "./layouts.map";

export interface SignupFlowLayoutProps extends Partial<ILayout> {}

const INITIAL_SLUG = SignupFlowSlug.CAT_NAMES;
const LOGIN_LINK = { id: "g-0", name: "Login", to: "/login" };

const FreshSignupFlow: FC<Readonly<SignupFlowLayoutProps>> =
({ children, head, lastBuilt }) => {
  const t = useTranslations("SUFv2.titles");
  const uxT = useTranslations("General.UX");
  const pathname = usePathname();
  const [ hideBackButton, setHideBackButton ] = useState(true);
  const [ mounted, setMounted ] = useState(false);

  const { canGoBack, goBack } = useFlowNavigation(pathname);

  const { cats, activeCatId, isStoreInitialised } = useSignupFlowStore((state) => ({
    activeCatId: state.activeCatId,
    cats: state.cats,
    isStoreInitialised: state.isStoreInitialised,
  }));

  const currentCat = cats[activeCatId];
  const { title, hideProgressSteps, hideFooter = true, bgColor = "light-grey", headerBgColor = "light" } = SIGNUP_FLOW_PAGES[pathname as SignupFlowSlug];

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    setHideBackButton(pathname === INITIAL_SLUG);
  }, [ pathname ]);

  const getTitle = () => {
    if (title && currentCat) {
      const variables = getInterpolationVariables({ name: currentCat?.name, gender: currentCat?.gender });
      return t(title, variables);
    }
    return t(title) || "";
  };

  return (
    <div>
      <DefaultHead
        seo={makePageTags(head?.seo)}
        /* @ts-ignore */
        hideFromSearch={head?.hide_from_search_engines}
        lastBuilt={lastBuilt}
        fallbackTags={head?.fallbackTags && makePageTags(head.fallbackTags)}
      />
      <Suspense>
        <QueryParamHandler />
      </Suspense>

      <Header
        {...HEADER_LINKS_NONE}
        hasAnnouncementBanner={false}
        guestLinks={hideBackButton ? [ LOGIN_LINK ] : []}
        historyNavigation={{
          back: () => (canGoBack ? goBack() : undefined),
          hide: hideBackButton,
        }}
        backgroundColor={headerBgColor}
      />

      <Main className={cx(themeRootClassMap[bgColor], "min-h-[calc(100dvh-var(--header-height-mobile))] xl:min-h-[calc(100dvh-var(--header-height-desktop))]")}>
        { !mounted || !isStoreInitialised ? (
          <div className="fixed inset-0 flex flex-col justify-center items-center gap-4">
            <LoadingSpinner color="black" size={32} />
            <TextSubtitle size={4}>{ uxT("loading") }</TextSubtitle>
          </div>
        ) : (
          <>
            { !hideProgressSteps && (
              <div className="bg-white">
                <div className="flex justify-center flex-col fixed top-[var(--header-height-mobile)] xl:top-[var(--header-height-desktop)] w-full z-20 left-1/2 -translate-x-1/2 px-14 lg:px-0 mx-auto bg-white">
                  <SimpleProgressSteps
                    steps={signupSteps}
                    currentSlug={pathname as SignupFlowSlug}
                    hideLabels={true}
                  />
                </div>
              </div>
            ) }

            { title ? (
              <div className="bg-white">
                <Spacer size={hideProgressSteps ? "md-lg" : "2xl"} />
                <TextTitle size={3} align="center" className="px-4">
                  { getTitle() }
                </TextTitle>
                <Spacer size={hideProgressSteps ? "md-lg" : "md"} />
              </div>
            ) : null }
            { children }
          </>
        ) }
      </Main>

      { !hideFooter && (
        <Footer />
      ) }
    </div>
  );
};

export default memo(FreshSignupFlow);
